import PropTypes from 'prop-types';
import React, { createContext, useEffect, useReducer } from 'react';
import pkg from '../../package.json';
import useToggleStyle from '../hooks/useToggleStyle';
import { applicationReducer } from '../reducers/applicationReducer';
import { getColor, getItemFromStore } from '../utils/utils';

/**
 * Application version
 */
export const version = pkg.version;

/**
 * Application name
 */
export const navbarBreakPoint = 'lg';

/**
 * Application top navbar breakpoint
 */
export const topNavbarBreakpoint = 'md';

/**
 * Application settings object
 *
 * @typedef {Object} ApplicationSettings
 * @property {Object} systemRoles - Object containing system roles
 * @property {boolean} isFluid - Flag indicating if the application is in fluid mode
 * @property {boolean} isRTL - Flag indicating if the application is in RTL (right-to-left) mode
 * @property {boolean} isDark - Flag indicating if the application is in dark mode
 * @property {boolean} isPrivacyMode - Flag indicating if the application is in privacy mode
 * @property {boolean} showGlobalFilters - Flag indicating if global filters are shown
 * @property {Object} globalFilters - Filters for persistence
 * @property {boolean} showSimpleFilters - Flag indicating if simple filters are shown
 * @property {string} lastTabOpen - The last tab that was open
 * @property {boolean} showBookmarks - Flag indicating if bookmarks are shown
 * @property {string} navbarPosition - The position of the navbar
 * @property {boolean} showBurgerMenu - Flag indicating if the burger menu is shown
 * @property {string} currency - The currency symbol
 * @property {boolean} isNavbarVerticalCollapsed - Flag indicating if the vertical navbar is collapsed
 * @property {string} navbarStyle - The style of the navbar
 * @property {boolean} showKeyboardShortcuts - Flag indicating if keyboard shortcuts are shown
 * @property {boolean} showHelpModal - Flag indicating if the help modal is shown
 * @property {boolean} showDevPages - Flag indicating if developer pages are shown
 */
export const applicationSettings = {
  systemRoles: {
    '64c22e2016d735abd7e349df': {
      slug: 'user',
      name: 'User'
    },
    '64c22e2f16d735abd7e349e0': {
      slug: 'admin',
      name: 'Admin'
    },
    '64c22e3c16d735abd7e349e1': {
      slug: 'super-admin',
      name: 'Super Admin'
    }
  },
  isFluid: true,
  isRTL: false,
  isDark: false,
  isPrivacyMode: false,
  showGlobalFilters: false,
  globalFilters: {},
  showSimpleFilters: false,
  showAddSavedSearchModal: false,
  showSavedSearchListModal: false,
  showShareSavedSearchModal: false,
  showExploreFieldsSidebar: false,
  lastTabOpen: 'Querybuilder',
  showBookmarks: false,
  ipLookUpOffCanvas: false,
  navbarCollapsed: false,
  navbarPosition: 'vertical',
  showBurgerMenu: false,
  showSettingPanel: false,
  currency: '$',
  isNavbarVerticalCollapsed: false,
  navbarStyle: 'card',
  showKeyboardShortcuts: false,
  showHelpModal: false,
  showDevPages: false,
  showNavigator: true,
  showProfileMenu: false,
  driver_onboarding: false,
  driver_onboarding_step: 0
};

/**
 * Create ApplicationContext
 * @type {React.Context<{}>}
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.1
 * @since 0.1.0-beta.1
 */
export const ApplicationContext = createContext(applicationSettings);

/**
 * Provides the application context and state to its children components
 *
 * @param {Object} props - The component props
 * @returns {JSX.Element} The rendered component
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.1
 * @since 0.1.0-beta.1
 */
const ApplicationProvider = props => {
  // Explicitly read isFluid from localStorage first to ensure we have the correct initial value
  const storedIsFluid = localStorage.getItem('isFluid');
  const initialIsFluid =
    storedIsFluid !== null
      ? JSON.parse(storedIsFluid)
      : applicationSettings.isFluid;

  const applicationState = {
    isFluid: initialIsFluid, // Use our explicitly retrieved value
    currency: getItemFromStore('currency', applicationSettings.currency),
    disabledNavbarPosition: [],
    driver_onboarding: getItemFromStore(
      'driver_onboarding',
      applicationSettings.driver_onboarding
    ),
    driver_onboarding_step: getItemFromStore(
      'driver_onboarding_step',
      applicationSettings.driver_onboarding_step
    ),
    globalFilters: getItemFromStore(
      'globalFilters',
      applicationSettings.globalFilters
    ),
    ipLookUpOffCanvas: getItemFromStore(
      'ipLookUpOffCanvas',
      applicationSettings.ipLookUpOffCanvas
    ),
    isDark: getItemFromStore('isDark', applicationSettings.isDark),
    isNavbarVerticalCollapsed: getItemFromStore(
      'isNavbarVerticalCollapsed',
      applicationSettings.isNavbarVerticalCollapsed
    ),
    isPrivacyMode: getItemFromStore(
      'isPrivacyMode',
      applicationSettings.isPrivacyMode
    ),
    isRTL: getItemFromStore('isRTL', applicationSettings.isRTL),
    lastTabOpen: getItemFromStore(
      'lastTabOpen',
      applicationSettings.lastTabOpen
    ),
    navbarCollapsed: getItemFromStore(
      'navbarCollapsed',
      applicationSettings.navbarCollapsed
    ),
    navbarPosition: getItemFromStore(
      'navbarPosition',
      applicationSettings.navbarPosition
    ),
    navbarStyle: getItemFromStore(
      'navbarStyle',
      applicationSettings.navbarStyle
    ),
    showAddSavedSearchModal: applicationSettings.showAddSavedSearchModal,
    showBookmarks: applicationSettings.showBookmarks,
    showBurgerMenu: getItemFromStore(
      'showBurgerMenu',
      applicationSettings.showBurgerMenu
    ),
    showDevPages: getItemFromStore(
      'showDevPages',
      applicationSettings.showDevPages
    ),
    showExploreFieldsSidebar: getItemFromStore(
      'showExploreFieldsSidebar',
      applicationSettings.showExploreFieldsSidebar
    ),
    showGlobalFilters: applicationSettings.showGlobalFilters,
    showHelpModal: applicationSettings.showHelpModal,
    showKeyboardShortcuts: getItemFromStore(
      'showKeyboardShortcuts',
      applicationSettings.showKeyboardShortcuts
    ),
    showNavigator: applicationSettings.showNavigator,
    showProfileMenu: applicationSettings.showProfileMenu,
    showSavedSearchListModal: applicationSettings.showSavedSearchListModal,
    showSettingPanel: applicationSettings.showSettingPanel,
    showSimpleFilters: applicationSettings.showSimpleFilters,
    systemRoles: applicationSettings.systemRoles
  };

  const [application, applicationDispatch] = useReducer(
    applicationReducer,
    applicationState
  );

  // Debug the isFluid state during initialization
  // useEffect(() => {
  //   console.log('ApplicationProvider: Initial isFluid =', initialIsFluid);
  //   console.log(
  //     'ApplicationProvider: Current state isFluid =',
  //     application.isFluid
  //   );
  // }, []);

  // Ensure isFluid changes are immediately saved to localStorage
  useEffect(() => {
    localStorage.setItem('isFluid', JSON.stringify(application.isFluid));
    // console.log(
    //   'ApplicationProvider: isFluid updated and saved =',
    //   application.isFluid
    // );
  }, [application.isFluid]);

  const { isLoaded } = useToggleStyle(
    application.isFluid,
    application.isRTL,
    application.isDark,
    application.navbarStyle,
    applicationDispatch
  );

  const setApplication = (key, value) => {
    // For isFluid specifically, directly save to localStorage in addition to dispatching
    if (key === 'isFluid') {
      localStorage.setItem('isFluid', JSON.stringify(value));
      // console.log('setApplication: Saving isFluid =', value);
    }

    applicationDispatch({
      type: 'SET_CONFIG',
      payload: {
        key,
        value,
        setInStore: [
          'isFluid',
          'driver_onboarding_step',
          'driver_onboarding',
          'globalFilters',
          'isDark',
          'isNavbarVerticalCollapsed',
          'isPrivacyMode',
          'isRTL',
          'lastTabOpen',
          'navbarPosition',
          'navbarStyle',
          //'showAddSavedSearchModal',
          //'showBookmarks',
          'showBurgerMenu',
          'showDevPages',
          'showExploreFieldsSidebar',
          //'showGlobalFilters',
          //'showHelpModal',
          'showKeyboardShortcuts'
          //'showNavigator',
          //'showProfileMenu',
          //'showSavedSearchListModal',
          //'showSimpleFilters'
        ].includes(key)
      }
    });
  };

  if (!isLoaded) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: application.isDark
            ? getColor('dark')
            : getColor('light')
        }}
      />
    );
  }

  return (
    <ApplicationContext.Provider
      value={{ application, setApplication, applicationDispatch, isLoaded }}>
      {props.children}
    </ApplicationContext.Provider>
  );
};

ApplicationProvider.propTypes = {
  children: PropTypes.node
};

export default ApplicationProvider;
